import React, { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Save, Printer, CreditCard, ChevronDown, SaveIcon } from 'lucide-react'
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useToast } from "@/components/ui/use-toast";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

import InvoiceHeader from "./invoice-header";
import InvoiceItemsDetails from "./invoice-items-body";
import { BaseItem, InvoiceDetails } from "@/types/invoice";
import {
  useCreateInvoiceMutation,
  useGetInvoiceByIdQuery,
  useUpdateInvoiceMutation,
  useLazyGetInvoiceByIdQuery,
} from "@/services/invoiceApi";
import { ErrorType } from "@/types";
import { InvoiceStatus, InvoiceType } from "@/types/enums";
import { PrinterIcon } from "lucide-react";
import { InvoiceExport } from "../../pos/sales_history/table/receipt-component";
import { useDispatch } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { setInvoice } from "@/redux/invoiceSlice";
import { format } from "date-fns";
import { capitalizeFirstLetter } from "@/lib/helper";
interface InvoiceForm {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: string;
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit" | "member-created">>;
  refetch: any;
  editModeData: InvoiceDetails | undefined;
}
const InvoiceForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  editModeData,
}: InvoiceForm) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const [isSaveDropdown, setIsSaveDropdown] = useState(false)
  const [invoiceId, setInvoiceId] = useState<number | null>(null);

  const userInfo = useSelector((state: RootState) => state.auth.userInfo?.user);
  const { toast } = useToast();
  const [createInvoice, result] = useCreateInvoiceMutation();
  const [updateInvoice, { isLoading, error }] = useUpdateInvoiceMutation();
  const [getInvoiceById] = useLazyGetInvoiceByIdQuery();
  const hydrateRetryRef = React.useRef(0);
  const hydrateTimerRef = React.useRef<number | null>(null);
  const initialValues = {
    org_id: orgId,
    invoice_number: "",
    member_id: 0,
    member_name: "",
    member_email: "",
    is_business: false,
    member_address: "",
    member_nic: "",
    member_gender: "",
    notes: "",
    total: 0,
    sub_total: 0,
    tax_amount: 0,
    discount: 0,
    status: InvoiceStatus.Pending,
    invoice_type: InvoiceType.Proforma,
    payment_date: (new Date()).toISOString(),
    id: 0,
    total_quantity: 0,
    items: [],
    seller_id: 0,
    seller_name: "",
    member_tax_group_id: 0,
    member_group_id: 0,
  };

  const form = useForm<InvoiceDetails>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    getValues,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = form;

  const watcher = watch()

  useEffect(() => {
    const hydrate = async () => {
      if (action == "edit") {
        // Prefer fetching a full invoice payload by ID to ensure items/price/tax are fully hydrated.
        // On slow networks this can fail intermittently; retry a couple times before falling back.
        if (editModeData?.id) {
          try {
            // If orgId isn't ready yet, wait for it (effect reruns when orgId updates)
            if (!orgId) {
              const payload = { ...editModeData } as InvoiceDetails;
              if (payload?.invoice_type == InvoiceType.Proforma) {
                payload.payment_date = (new Date()).toISOString()
              }
              reset(payload);
              return;
            }

            const invoice = await getInvoiceById({ id: editModeData.id, org_id: orgId }).unwrap();
            const payload = { ...invoice } as InvoiceDetails;
            if (payload.invoice_type == InvoiceType.Proforma) {
              payload.payment_date = (new Date()).toISOString()
            }
            hydrateRetryRef.current = 0;
            reset(payload);
            return;
          } catch (e) {
            // Retry a couple times (helps when the request fails due to slow/intermittent connection)
            const nextTry = hydrateRetryRef.current + 1;
            hydrateRetryRef.current = nextTry;

            if (hydrateTimerRef.current) {
              window.clearTimeout(hydrateTimerRef.current);
              hydrateTimerRef.current = null;
            }

            if (nextTry <= 2 && orgId) {
              hydrateTimerRef.current = window.setTimeout(() => {
                // Only retry if still in edit mode and sheet is open
                if (action === "edit" && isOpen && editModeData?.id) {
                  getInvoiceById({ id: editModeData.id, org_id: orgId })
                    .unwrap()
                    .then((invoice) => {
                      const payload = { ...invoice } as InvoiceDetails;
                      if (payload.invoice_type == InvoiceType.Proforma) {
                        payload.payment_date = (new Date()).toISOString()
                      }
                      hydrateRetryRef.current = 0;
                      reset(payload);
                    })
                    .catch(() => {
                      // keep failing; the main effect run will handle fallback
                    });
                }
              }, 600 * nextTry);
            }

            // Temporary fallback so the user sees something; API fetch will overwrite on success.
            const payload = { ...editModeData } as InvoiceDetails;
            if (payload?.invoice_type == InvoiceType.Proforma) {
              payload.payment_date = (new Date()).toISOString()
            }
            reset(payload);
            return;
          }
        }

        const payload = { ...editModeData } as InvoiceDetails;
        if (payload?.invoice_type == InvoiceType.Proforma) {
          payload.payment_date = (new Date()).toISOString()
        }
        reset(payload);
    } else if (action == "member-created") {
      reset(editModeData as InvoiceDetails);
    } else {
      // For "add" action, reset to initial values but set seller to current user
      hydrateRetryRef.current = 0;
      if (hydrateTimerRef.current) {
        window.clearTimeout(hydrateTimerRef.current);
        hydrateTimerRef.current = null;
      }
      reset({
        ...initialValues,
        seller_id: userInfo?.id || 0,
        seller_name: userInfo?.first_name || "",
      });
    }
    };

    hydrate();

    return () => {
      if (hydrateTimerRef.current) {
        window.clearTimeout(hydrateTimerRef.current);
        hydrateTimerRef.current = null;
      }
    };
  }, [action, editModeData, reset, userInfo, getInvoiceById, orgId]);

  const onSubmit = async (payload: InvoiceDetails) => {
    payload.payment_date = format(new Date(), "yyyy-MM-dd")

    if (payload.member_id == 0) {
      toast({
        variant: "destructive",
        title: "Error in Submission",
        description: "Please select a member",
      })
      return;
    }

    if (!payload.items || payload.items.length === 0) {
      toast({
        variant: "destructive",
        title: "Error in Submission",
        description: "Please add at least one item",
      })
      return;
    }

    if (payload.items.find((invoice: BaseItem) => !invoice.item_id || invoice.item_id === 0)) {
      toast({
        variant: "destructive",
        title: "Error in Submission",
        description: "Please select a product or membership for all items",
      })
      return;
    }

    if (payload.items.find((invoice: BaseItem) => !invoice.booking_date || invoice.booking_date === '')) {
      toast({
        variant: "destructive",
        title: "Error in Submission",
        description: "Please select a valid booking date for all items",
      })
      return;
    }

    // Validate flexible price products before other validations
    const flexiblePriceItemsWithoutPrice = payload.items.filter((item: BaseItem) => {
      return item.flexible_price === true && (!item.price || item.price === undefined || item.price === null || item.price <= 0);
    });

    if (flexiblePriceItemsWithoutPrice.length > 0) {
      const firstInvalidItem = flexiblePriceItemsWithoutPrice[0];
      const itemName = capitalizeFirstLetter(firstInvalidItem.description as string || `Item (ID: ${firstInvalidItem.item_id})`);
      toast({
        variant: "destructive",
        title: "Price Required",
        description: `Please enter a valid price for ${itemName}. Flexible pricing products require a price to be set.`,
      });
      return;
    }

    for (const item of payload.items) {
      const {
        quantity,
        description,
        price,
        flexible_price,
        item_id,
        item_type,
      } = item as BaseItem;
      const itemName = capitalizeFirstLetter(description as string || 'Item');

      // Double-check flexible price products have a price entered (redundant but safe)
      if (flexible_price === true) {
        if (!price || price === undefined || price === null || price <= 0) {
          toast({
            variant: "destructive",
            title: "Price Required",
            description: `Please enter a valid price`,
          });
          return;
        }
      }

      // Validate quantity
      if (quantity > 1000) {
        toast({
          variant: "destructive",
          title: `${itemName}: quantity must not exceed 1000.`,
        });
        return;
      }

      if (quantity <= 0) {
        toast({
          variant: "destructive",
          title: `${itemName}: quantity must be more than 0.`,
        });
        return;
      }
    }

    // Transform items to minimal SellableItem structure (remove tax calculation fields)
    // But keep flexible_price for validation check before transformation
    const minimalItems = payload.items.map((item: BaseItem) => {
      const minimal: any = {
        item_id: item.item_id,
        contract_id: item.contract_id,
        item_type: item.item_type,
        quantity: item.quantity,
        discount: item.discount,
        booking_date: item.booking_date,
      };
      // Only include price for flexible price wallet products
      if (item.flexible_price && item.price !== undefined && item.price > 0) {
        minimal.price = item.price;
      }
      return minimal;
    });

    // Create payload with minimal items
    const apiPayload = {
      ...payload,
      items: minimalItems,
    };


    try {
      if (action === "add" || action === "member-created") {
        const resp = await createInvoice(apiPayload).unwrap();
        if (resp.status_code == 200) {
          toast({
            variant: "success",
            title: "Invoice created successfully",
          });
          setInvoiceId(resp.id);
          return resp
        }
      } else if (action === "edit") {
        const resp = await updateInvoice(apiPayload).unwrap();
        if (resp.status_code === 200) {
          toast({
            variant: "success",
            title: "Invoice updated Successfully",
          });
          setInvoiceId(payload.id as number);
          return resp
        }
      }



    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
        return false
      } else {
        toast({
          variant: "destructive",
          title: "Error in Submission",
          description: `Something Went Wrong.`,
        });
        return false
      }
    }
  };



  const handleSaveAndPrint = async () => {
    const formValues = getValues();
    const resp = await onSubmit(formValues);
    if (resp && resp.status_code === 200) {
      // Fetch the complete invoice data using the lazy query
      const { data: invoiceData } = await getInvoiceById({ id: resp.id, org_id: orgId });

      if (invoiceData) {
        // Use fetched invoice data for printing (contains all tax info)
        handlePrintInvoice(invoiceData);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch invoice data for printing",
        });
      }
    }
  };


  const handlePrintInvoice = async (invoiceDetails: InvoiceDetails) => {
    // Assuming `InvoiceExport` generates HTML content for the invoice
    const htmlContent = await InvoiceExport(invoiceDetails);

    // Create an invisible iframe
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0px";
    iframe.style.height = "0px";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const iframeDoc =
      iframe.contentWindow?.document || iframe.contentDocument;
    if (iframeDoc) {
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();

      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();

        setTimeout(() => {
          document.body.removeChild(iframe); // Cleanup
          if (action == "member-created") {
            navigate("/admin/finances")
          } else {
            handleClose()
          }
        }, 100);
      };
    }
  };

  const handleSaveOnly = async () => {
    const formValues = getValues();
    const resp = await onSubmit(formValues);
    if (resp && resp.status_code === 200 || resp && action == "edit") {
      handleClose(); // Close form after saving

      if (action == "member-created") {
        setTimeout(() => {
          navigate("/admin/finances")
        }, 500)
      }
    }
  };

  const handleSaveAndPay = async () => {
    const formValues = getValues();
    const resp = await onSubmit(formValues);
    if (resp && resp.status_code === 200) {
      // Fetch the complete invoice data using the lazy query
      const { data: invoiceData } = await getInvoiceById({ id: resp.id, org_id: orgId });

      // Set the complete invoice data in redux state
      if (invoiceData) {
        dispatch(setInvoice(invoiceData));
      }

      setTimeout(() => {
        navigate('/counter-selection')
      }, 500)

      handleClose(); // Close form after saving
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    clearErrors();
    setAction("add");
    // Reset to initial values with cleared member and seller fields
    reset(
      {
        ...initialValues,
        member_id: 0,
        member_name: "",
        member_email: "",
        member_address: "",
        member_nic: "",
        member_gender: "",
        is_business: false,
        seller_id: 0,
        seller_name: "",
        member_tax_group_id: 0,
      },
      {
        keepIsSubmitted: false, // Reset the form's submission state
        keepSubmitCount: false, // Reset the count of submissions
        keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
        keepDefaultValues: false, // Reset default values as well
      }
    );

    setOpen(false);
  };

  console.log({ watcher, errors, action })

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg lg:!max-w-[1300px] p-0 custom-scrollbar h-full w-full !max-w-full sm:!max-w-4xl"
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4 "
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
              <SheetTitle>
                  <div className="flex items-center justify-between gap-2 w-full">
                  <div className="flex items-start gap-3">
                    <button type="button" onClick={handleClose}>
                      <i className="fa fa-xmark text-xl"></i>
                    </button>
                    <div>
                      <p className="font-semibold text-base sm:text-lg">Invoice</p>
                      <div className="text-sm">
                        <span className="text-gray-400 pr-1 font-semibold">
                          Dashboard
                        </span>{" "}
                        <span className="text-gray-400 font-semibold">/</span>
                        <span className="pl-1 text-primary font-semibold ">
                          {action == "add" || action == "member-created" ? "Create" : "Edit"} Invoice
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">

                    <DropdownMenu open={isSaveDropdown} onOpenChange={setIsSaveDropdown}>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" className="cursor-pointer bg-primary hover:bg-primary flex items-center gap-2 h-9 px-2 sm:px-3">
                          <i className="fa-regular fa-floppy-disk text-base"></i>
                          <span className="hidden sm:inline">Save</span>
                          <ChevronDown className="h-4 w-4 opacity-70" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-auto">
                        <DropdownMenuItem asChild onClick={handleSaveOnly}>
                          <Button
                            className="cursor-pointer w-full hover:bg-slate-100 bg-white text-black  flex justify-start   gap-2 h-8 "
                          >
                            <SaveIcon className="size-4" />
                            <span className=" ">
                              {action === "edit" ? "Update" : "Save"}
                            </span>
                          </Button>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild onClick={handleSaveAndPrint} >
                          <Button
                            className="cursor-pointer w-full hover:bg-slate-100 bg-white text-black  text-nowrap flex justify-start  gap-2 h-8 my-2"
                          >
                            <PrinterIcon className="size-4" />
                            <span className="">
                              {action === "edit" ? "Update & Print" : "Save & Print"}
                            </span>
                          </Button>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild onClick={handleSaveAndPay}>
                          <Button
                            className="cursor-pointer w-full hover:bg-slate-100 bg-white text-black  text-nowrap justify-start flex  gap-2 h-8"
                          >
                            <CreditCard className="size-4" />
                            <span className="">
                              {action === "edit" ? `Update & Pay` : "Save & Pay"}
                            </span>
                          </Button>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <Separator className=" h-[1px] rounded-full     " />

            <SheetDescription className=" h-full  p-6  ">
              <InvoiceHeader
                setValue={setValue}
                control={control}
                errors={errors}
                watch={watch}
                action={action}
              />
              <div className="py-4"></div>
              <InvoiceItemsDetails
                setValue={setValue}
                control={control}
                errors={errors}
                watch={watch}
                action={action}
              />
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default InvoiceForm;
